import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import i18n from '@/i18n'
import { resolveApiErrorMessage } from '@/i18n/api-errors'
import { API_BASE_URL } from '@/lib/constants'
import { isRedirectResponse } from '@/lib/api'
import { handOffToDeployment } from '@/lib/handoff'
import { queryClient } from '@/lib/queryClient'
import { useGeneratorStore } from './generatorStore'

interface User {
  id: string
  email: string
  hasUsage?: boolean
  isAdmin?: boolean
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  loginWithWrks: (wrksToken: string, source: 'gov' | 'wrks', devMode?: boolean) => Promise<void>
  /** OAuth 콜백 후: 백엔드가 이미 세션 쿠키를 설정했으므로 /auth/me 로 사용자만 읽어 스토어를 채운다 */
  completeOAuthLogin: () => Promise<void>
  loginWithFreepass: () => Promise<void>
  logout: () => Promise<void>
  setUser: (user: User | null) => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,

      loginWithWrks: async (wrksToken: string, source: 'gov' | 'wrks', devMode?: boolean) => {
        const response = await fetch(`${API_BASE_URL}/auth/wrks-sso`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ wrksToken, source, devMode }),
          credentials: 'include',
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(
            errorData.message
              ? resolveApiErrorMessage({
                  status: response.status,
                  code: errorData.errorCode,
                  params: errorData.params,
                  serverMessage: errorData.message,
                })
              : i18n.t('errors.SSO_LOGIN_FAILED')
          )
        }

        const data = await response.json()

        // 잘못된 배포로 로그인하면 백엔드가 올바른 도메인으로 redirect를 지시한다.
        // 반환된 Promise 는 끝나지 않는다: 호출부가 다음 화면으로 넘어가면 이 이동이 취소된다.
        if (isRedirectResponse(data)) {
          return handOffToDeployment(data.redirect)
        }

        set({
          user: data.user,
          isAuthenticated: true,
        })
      },

      completeOAuthLogin: async () => {
        const response = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include' })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(
            errorData.message
              ? resolveApiErrorMessage({
                  status: response.status,
                  code: errorData.errorCode,
                  params: errorData.params,
                  serverMessage: errorData.message,
                })
              : i18n.t('errors.SSO_LOGIN_FAILED')
          )
        }

        const data = await response.json()

        // 이전 사용자의 캐시가 남아 있을 수 있으므로 초기화
        queryClient.clear()

        set({
          user: { id: data.id, email: data.email, hasUsage: data.hasUsage, isAdmin: data.isAdmin },
          isAuthenticated: true,
        })
      },

      loginWithFreepass: async () => {
        const response = await fetch(`${API_BASE_URL}/auth/freepass`, {
          method: 'POST',
          credentials: 'include',
        })

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}))
          throw new Error(
            errorData.message
              ? resolveApiErrorMessage({
                  status: response.status,
                  code: errorData.errorCode,
                  params: errorData.params,
                  serverMessage: errorData.message,
                })
              : i18n.t('errors.FREEPASS_FAILED')
          )
        }

        const data = await response.json()

        set({
          user: data.user,
          isAuthenticated: true,
        })
      },

      logout: async () => {
        try {
          await fetch(`${API_BASE_URL}/auth/logout`, {
            method: 'POST',
            credentials: 'include',
          })
        } catch {
          // 로그아웃 실패해도 로컬 상태는 초기화
        }

        queryClient.clear()
        useGeneratorStore.getState().reset()

        set({
          user: null,
          isAuthenticated: false,
        })
      },

      setUser: (user) => set({ user, isAuthenticated: !!user }),
    }),
    {
      name: 'vixgen-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
